import {
  AppTableBody,
  AppTableCell,
  AppTableHeader,
  AppTableHeaderCell,
  AppTableRoot,
  AppTableRow,
  type AppTableRowProps,
  BorderTableCell,
  BorderTableRow,
} from '@components/AppTable';
import PermissionTooltip from '@components/PermissionTooltip';
import TextCopyToClipboard from '@components/TextCopyToClipboard';
import { FlatButton } from '@elements/Button';
import { DarkPopup } from '@elements/Popup';
import useUrlPermission from '@hooks/url-permission';
import { TrashIcon, WarningCircle } from '@phosphor-icons/react';
import { Badge, Button, cn, Spinner } from '@skupreme/shared/ui';
import { ReadOnlyTip, useOrdersV2 } from '@features/orders/hooks/orders-v2';
import { CommonUtility } from '@utility/common';
import { forwardRef, useMemo, useState } from 'react';
import { DateFormat, DateUtility } from '@utility/date';
import { carrierIcons, OrderStatus } from '@utility/constants/common';
import { MarketplaceIcon } from '@components/ConnectedMarketplaces';
import { ImageWithFallback } from '@components/FallbackImage';
import { ActionDropdown, ActionDropdownMenu } from '@elements/Dropdown';
import { IconSizes } from '@utility/constants/style-constants';
import { OrderTime } from '@page-components/order';
import { IconMarketplace, getOrderError } from '@page-components/order/common';
import { ItemInfoPopup } from '@page-components/order/ListInfoPopup';
import { Link } from 'react-router';

type BatchOrderTableRowProps = AppTableRowProps & {
  greyed?: number;
  checked?: number;
};

/** An order inside an expanded batch; tinted while picked or greyed out. */
export const BatchOrderTableRow = forwardRef<
  HTMLTableRowElement,
  BatchOrderTableRowProps
>(function BatchOrderTableRowPart(
  { greyed, checked, className, ...rest },
  ref,
) {
  return (
    <BorderTableRow
      ref={ref}
      {...rest}
      className={cn(
        'sk:group sk:text-xs',
        (checked || greyed) && 'sk:bg-fill-tertiary',
        className,
      )}
    />
  );
});

const MUTED = 'sk:text-fg-tertiary';

function OrderRow({
  item,
  batchId,
  selectedOrder,
  toggleSelection,
  deleteOrder,
  rateLoaders,
  orderRates,
  rateKey,
  isShipped,
  showActions,
}) {
  const hasAction = !isShipped && showActions;
  /* EN-4117: removing an order from a batch, or labelling it, writes. */
  const { isV2 } = useOrdersV2();

  const { disabled, hidden } = useUrlPermission(`/app/orders/list/${status}`);

  const rates = orderRates[rateKey];

  const [hasImage, setHasImage] = useState(true);

  const onNoImage = () => {
    setHasImage(false);
  };

  const isCompleted = item.status === OrderStatus.Completed;

  const lowestRate = useMemo(() => {
    if (!CommonUtility.isValidObject(rates)) {
      return null;
    }
    let lowRate = Infinity;
    let serviceName = '';
    const weight = { lb: 0, oz: 0 };

    Object.keys(rates.packages).forEach(serviceKey => {
      const service = rates?.packages[serviceKey];
      service.forEach(rateCard => {
        if (CommonUtility.isValidNumber(rateCard.rate)) {
          if (rateCard.rate < lowRate) {
            lowRate = rateCard.rate;
            serviceName = serviceKey;
            weight.lb = rateCard.pounds;
            weight.oz = rateCard.ounces;
          }
        }
      });
    });

    return {
      rate: lowRate,
      service: serviceName,
      weight,
    };
  }, [rates]);

  const orderId = (
    <span className="sk:flex sk:items-center">
      {item.marketplace_order_display_id}
      <TextCopyToClipboard
        showText={false}
        text={item.marketplace_order_display_id}
        iconSize={IconSizes.medium}
        copyTooltip="Copy Order ID"
      />
    </span>
  );

  const moreItems = item.items.length - 3;

  const only3Items = useMemo(() => item.items.slice(0, 2), [item]);

  const hasValidRate = CommonUtility.isValidNumber(
    lowestRate?.rate || item.label?.rate,
  );

  const selectOrder = () => {
    if (hasAction && !disabled) {
      toggleSelection(item._id, batchId, item.error);
    }
  };

  return (
    <BatchOrderTableRow
      className="action-hover"
      verticalAlign="middle"
      checked={selectedOrder && selectedOrder[item._id] ? 1 : 0}
      greyed={(isShipped || item.loading) && !isShipped ? 1 : 0}
    >
      <BorderTableCell className="sk:px-2.5!">
        <div className="border" />
        <div className="sk:flex sk:flex-wrap sk:items-center sk:gap-1.5">
          <Link
            to={`/app/orders/${encodeURIComponent(item._id)}`}
            className={cn(
              'sk:font-bold',
              !hasAction || item.loading
                ? 'sk:text-fg'
                : 'sk:text-fg-tertiary sk:underline',
            )}
          >
            {orderId}
          </Link>
          {item.error && (
            <DarkPopup
              trigger={
                <WarningCircle
                  weight="fill"
                  size={18}
                  className="sk:text-danger"
                />
              }
            >
              {getOrderError(item.order_error, isShipped)}
            </DarkPopup>
          )}
          {isShipped && showActions && (
            <Badge tone="neutral">
              {CommonUtility.removeUnderscoreAndCapitalize(item.status)}
            </Badge>
          )}
        </div>
      </BorderTableCell>
      <AppTableCell>
        <OrderTime time={item.time_acknowledged} isEnabled />
        <div className={MUTED}>
          {DateUtility.formatDate(item.time_acknowledged, DateFormat.date)}
        </div>
      </AppTableCell>
      <AppTableCell textAlign="left">
        <IconMarketplace
          className={!hasImage || item.marketplace === 'manual' ? 'manual' : ''}
        >
          {/* @ts-expect-error marketplace prop typing differs from runtime value */}
          <MarketplaceIcon
            marketplace={{ type: item.marketplace }}
            onNoImageFunction={onNoImage}
          />
        </IconMarketplace>
        {item.loading && (
          <div className="sk:flex sk:items-center sk:justify-center sk:gap-2">
            <Spinner size="sm" />
            <span>Processing</span>
          </div>
        )}
      </AppTableCell>
      <AppTableCell className="sk:pl-2.5!" textAlign="center">
        {!isCompleted && (
          <div className="sk:flex sk:items-center sk:gap-2">
            {!!carrierIcons[lowestRate?.service || item.label?.carrier] && (
              <span className="sk:flex sk:size-9 sk:shrink-0 sk:items-center sk:justify-center sk:rounded-full sk:bg-fill-tertiary">
                <img
                  src={carrierIcons[lowestRate?.service || item.label?.carrier]}
                  alt=""
                  className="sk:size-6 sk:object-contain"
                />
              </span>
            )}
            <span className="sk:uppercase">
              {lowestRate?.service || item.label?.carrier}
            </span>
          </div>
        )}
      </AppTableCell>
      <AppTableCell className="sk:pl-4!">
        {isCompleted ? (
          <span className={MUTED}>{isShipped ? 'Shipped' : 'In Progress'}</span>
        ) : rateLoaders[item._id] ||
          !CommonUtility.isNotNull(rateLoaders[item._id]) ? (
          <Spinner size="sm" />
        ) : (
          <div>
            {hasValidRate ? (
              <span className="sk:font-bold">
                {CommonUtility.currencyFormat(
                  lowestRate?.rate || item.label?.rate,
                )}
              </span>
            ) : (
              <span className={MUTED}>N/A</span>
            )}
            <div className={cn('sk:flex sk:flex-wrap sk:gap-x-2', MUTED)}>
              <span>
                {CommonUtility.roundNumber(
                  lowestRate?.weight?.lb || item?.label?.weight?.pounds || 0,
                )}{' '}
                lb
              </span>
              <span>
                {CommonUtility.roundNumber(
                  lowestRate?.weight?.oz || item?.label?.weight?.ounces || 0,
                )}{' '}
                oz
              </span>
            </div>
          </div>
        )}
      </AppTableCell>
      <AppTableCell className="sk:pl-4!">
        <ItemInfoPopup items={item?.items} total_items={item?.total_items} />
      </AppTableCell>
      <AppTableCell className="action-cell sk:pl-4!">
        <div className="sk:flex sk:items-center sk:justify-between sk:gap-2">
          <div className="sk:flex sk:flex-col sk:gap-1.5">
            {only3Items?.map(orderItem => (
              <div
                key={orderItem._id ?? orderItem.item_id}
                className="sk:flex sk:items-center sk:gap-2 sk:whitespace-nowrap"
              >
                <span className={MUTED}>{orderItem.qty} x</span>
                <span className="sk:flex sk:size-5 sk:shrink-0 sk:overflow-hidden sk:rounded-xs sk:bg-fill-tertiary">
                  <ImageWithFallback
                    src={orderItem?.p_images?.display_image}
                    className="sk:size-5 sk:object-contain"
                  />
                </span>
                <Link
                  to={`/app/catalogs/catalog/${orderItem.item_id}/?tab=package-attributes`}
                  className="sk:max-w-25 sk:truncate sk:text-link sk:underline"
                >
                  {orderItem.sku}
                </Link>
              </div>
            ))}
            {moreItems > 0 && <span className={MUTED}>+{moreItems} More</span>}
          </div>
          {!isShipped && (
            <div className="sk:flex sk:items-center sk:gap-2">
              {hasAction && (
                <ActionDropdown
                  className="sk:opacity-0 sk:group-hover:opacity-100 sk:focus-visible:opacity-100 sk:max-md:opacity-100"
                  direction="left"
                >
                  <ActionDropdownMenu>
                    <PermissionTooltip
                      disabled={disabled}
                      hidden={hidden}
                      position="top right"
                      wrapperClassName="sk:p-0!"
                    >
                      <ReadOnlyTip>
                        <FlatButton
                          onClick={() => deleteOrder(item, batchId)}
                          disabled={isV2}
                          className="dropdown-action dropdown-danger-action"
                        >
                          <TrashIcon /> Delete
                        </FlatButton>
                      </ReadOnlyTip>
                    </PermissionTooltip>
                  </ActionDropdownMenu>
                </ActionDropdown>
              )}
              {hasAction && (
                <PermissionTooltip
                  disabled={disabled}
                  hidden={hidden}
                  position="top right"
                  wrapperClassName="sk:p-0!"
                >
                  <ReadOnlyTip>
                    <Button
                      variant="primary"
                      isDisabled={isV2}
                      onPress={selectOrder}
                    >
                      View & Approve
                    </Button>
                  </ReadOnlyTip>
                </PermissionTooltip>
              )}
            </div>
          )}
        </div>
      </AppTableCell>
    </BatchOrderTableRow>
  );
}

const HEAD_CELL =
  'sk:px-0.75! sk:py-1.25! sk:text-xs sk:font-normal! sk:text-fg-tertiary';

export function ChildBatchOrderList({
  orders,
  selectedOrder,
  toggleSelection,
  batchId,
  deleteOrder,
  rateLoaders,
  orderRates,
  fulfillment_provider_id,
  isShipped,
}) {
  return (
    <AppTableRow>
      <AppTableCell
        colSpan={5}
        className="table-container-cell sk:overflow-visible! sk:border-t-0! sk:pt-0!"
      >
        <AppTableRoot
          fixed
          basic
          className="sk:[&_tbody_td]:px-0.75! sk:[&_tbody_td]:py-1.25!"
        >
          <AppTableHeader>
            <AppTableRow>
              <AppTableHeaderCell
                className={cn(HEAD_CELL, 'sk:px-2.5!')}
                width={2}
              >
                Order ID
              </AppTableHeaderCell>
              <AppTableHeaderCell className={HEAD_CELL} width={2}>
                Order Age
              </AppTableHeaderCell>
              <AppTableHeaderCell className={HEAD_CELL} width={1}>
                Marketplace
              </AppTableHeaderCell>
              <AppTableHeaderCell
                className={cn(HEAD_CELL, 'sk:pl-2.5!')}
                width={2}
              >
                Carrier
              </AppTableHeaderCell>
              <AppTableHeaderCell
                className={cn(HEAD_CELL, 'sk:pl-4!')}
                width={1}
              >
                Shipping Cost
              </AppTableHeaderCell>
              <AppTableHeaderCell
                className={cn(HEAD_CELL, 'sk:pl-4!')}
                width={1}
              >
                Total Items
              </AppTableHeaderCell>
              <AppTableHeaderCell
                className={cn(HEAD_CELL, 'sk:pl-4!')}
                width={3}
              >
                Contents
              </AppTableHeaderCell>
            </AppTableRow>
          </AppTableHeader>
          <AppTableBody>
            {(orders || []).map(orderItem => (
              <OrderRow
                item={orderItem}
                key={orderItem._id}
                selectedOrder={selectedOrder}
                toggleSelection={toggleSelection}
                batchId={batchId}
                deleteOrder={deleteOrder}
                rateLoaders={rateLoaders}
                orderRates={orderRates}
                rateKey={`${orderItem._id}-${fulfillment_provider_id}`}
                isShipped={orderItem.state === OrderStatus.Shipped}
                showActions={!isShipped}
              />
            ))}
          </AppTableBody>
        </AppTableRoot>
      </AppTableCell>
    </AppTableRow>
  );
}
